import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { redeemSignupContinue } from 'api/auth'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalContext from 'contexts/ModalContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import AccountAccessModal from './AccountAccessModal'

interface Props { token: string }
type Result = { email?: string }

// The URL hook hands the token to this modal and removes it from the URL so a
// temporary failure can be retried without exposing it in subsequent navigation.
export default function SignupContinueModal({ token }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { open, resolve } = useModalElementContext()
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)
  const [result, setResult] = useState<Result>()
  const request = useRef<{ attempt: number, promise: Promise<string | undefined> } | undefined>(undefined)
  const handoffStarted = useRef(false)
  const retry = useCallback(() => {
    setFailed(false)
    setAttempt(value => value + 1)
  }, [])
  const enterEmail = useCallback(() => setResult({}), [])

  useEffect(() => {
    if (result || !open) return
    let active = true
    // Reuse the in-flight prefill request when React replays effects.
    if (request.current?.attempt !== attempt) {
      request.current = { attempt, promise: redeemSignupContinue(token) }
    }
    request.current.promise.then(email => {
      if (active) setResult({ email })
    }).catch(() => {
      if (active) setFailed(true)
    })
    return () => { active = false }
  }, [attempt, result, token, open])

  useEffect(() => {
    if (!open || !result || handoffStarted.current) return
    handoffStarted.current = true
    // Queue the signup form before closing this dialog. Replacing ModalBase
    // in place would run its unmount callback and clear the provider's element.
    showModal(<AccountAccessModal initialMode="login" defaultEmail={result.email} autoContinueEmail={!!result.email} />)
    resolve(undefined)
  }, [open, result, showModal, resolve])

  return <ModalBase size="S" modalId="signupContinue">
    <ModalHeader
      dismissible
      title={intl.formatMessage({
        id: '3gM2pERjRLJ7Sda3NiLX',
        defaultMessage: 'Continue creating your account',
        description: 'Heading while opening the signup form from an unfinished signup reminder',
      })}
    />
    <ModalBody><ModalContent><VerticalSpacer gap={16}>
      <BodyText variant="medium" role="status">
        {failed ? <FormattedMessage
          id="zjphdl3QBqoCKHNjxMiV"
          defaultMessage="We couldn’t open your signup link. Try again or enter your email to continue."
          description="Temporary reminder link failure with retry or manual email entry options"
        /> : <FormattedMessage
          id="DQi3zdbbJ7Xjrzc-GUb7"
          defaultMessage="Opening your signup link…"
          description="Loading status while exchanging a reminder link for the signup email"
        />}
      </BodyText>
      {failed && <TextButton kind="primary" onClick={retry}><FormattedMessage
        id="dPiBWC4-l-Seps3r0lmq"
        defaultMessage="Try again"
        description="Button to retry a temporarily unavailable signup reminder link"
      />
      </TextButton>}
      <TextButton kind="tertiary" onClick={enterEmail}><FormattedMessage
        id="i9jgWNCMTfc47ANcBJ7P"
        defaultMessage="Enter email instead"
        description="Button to open the normal signup form without waiting for the reminder link"
      />
      </TextButton>
    </VerticalSpacer>
    </ModalContent>
    </ModalBody>
  </ModalBase>
}
